/**
 * Outil « Gaz et pression » (Boîte à outils, physique-chimie 1re et Tle).
 *
 * Explorer :
 * - Seringue : de l'air enfermé, ses particules qui s'agitent et frappent la paroi, le
 *   manomètre ; en 1re, on pousse ou tire le piston (Mariotte, P en fonction de V ou de 1/V) ;
 *   en Tle, isotherme / isochore / isobare, température absolue, volume molaire.
 * - Profondeur (1re) : un plongeur descend ; pression P = P_atm + ρ g h, force sur le tympan.
 * Défi : une question par sorte selon le niveau. Logique : `gas.ts`.
 */

import { useEffect, useState } from 'react';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import { Note, ParamSlider, Segmented, ToggleChip } from '../../ui/controls';
import { frenchSpaces } from '../../ui/format';
import { McqChallenge } from '../../ui/mcq-challenge';
import { RichText } from '../../ui/rich-text';
import { FIGURE_BG_CLASS, HaloText } from '../../ui/svg-parts';
import {
  EARDRUM,
  G,
  LIQUIDS,
  MODES,
  N_AIR,
  P_ATM,
  T0,
  THETA0,
  THETA_RANGE,
  V0_ML,
  V_RANGE,
  airDensity,
  bar,
  celsius,
  depthLines,
  depthPressure,
  fr,
  gasState,
  hPa,
  idealGas,
  kelvin,
  makeQuiz,
  molarVolume,
  partsFor,
  quizKinds,
  syringeLines,
  type GasState,
  type Mode,
  type Part,
} from './gas';

const W = 340;
const INK = 'var(--color-ink-700)';
const MUTED = 'var(--color-ink-500)';
const ACCENT = 'var(--color-primary-500)';
const HOT = 'var(--color-error-500)';

function Lines({ lines }: { lines: string[] }) {
  return (
    <Note>
      {lines.map((l) => (
        <span key={l} className="mb-1 block last:mb-0" data-testid="gs-law">
          <RichText>{l}</RichText>
        </span>
      ))}
    </Note>
  );
}

function Readout({ rows }: { rows: Array<[string, string]> }) {
  return (
    <table className="w-full text-sm" data-testid="gs-readings">
      <tbody>
        {rows.map(([k, v]) => (
          <tr key={k} className="border-ink-100 border-b last:border-0">
            <th scope="row" className="text-ink-600 py-1.5 pr-2 text-left font-normal">
              <RichText>{k}</RichText>
            </th>
            <td className="text-ink-900 py-1.5 text-right font-semibold tabular-nums">
              {frenchSpaces(v)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** horloge (s) pour l'agitation des particules ; arrêtée si `still` ou si l'élève préfère moins d'animation */
function useClock(still: boolean) {
  const [t, setT] = useState(0);
  useEffect(() => {
    if (still || typeof requestAnimationFrame !== 'function') return;
    if (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    )
      return;
    let raf = 0;
    const start = performance.now();
    const step = (now: number) => {
      setT((now - start) / 1000);
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [still]);
  return t;
}

const bounce = (v: number, lo: number, hi: number) => {
  const span = Math.max(1e-6, hi - lo);
  const m = (((v - lo) % (2 * span)) + 2 * span) % (2 * span);
  return lo + (m <= span ? m : 2 * span - m);
};

/* ---------- seringue ---------- */

const X0 = 80;
const PX_PER_ML = 3.5;
const CY0 = 38;
const CY1 = 96;
const N_DOTS = 26;
const DOTS = Array.from({ length: N_DOTS }, (_, i) => ({
  // suite R2 : points bien répartis dans le rectangle
  x: (0.5 + i * 0.7548776662) % 1,
  y: (0.5 + i * 0.569840291) % 1,
  a: (i * 2.399) % (2 * Math.PI),
}));

function Manometer({ P }: { P: number }) {
  const cx = 38;
  const cy = 66;
  const r = 27;
  const max = 6000; // hPa
  const ang = (-120 + (Math.min(P / 100, max) / max) * 240) * (Math.PI / 180);
  return (
    <g data-testid="gs-manometer">
      <circle cx={cx} cy={cy} r={r} fill="var(--color-ink-50)" stroke={INK} strokeWidth={1.5} />
      {[0, 1000, 2000, 3000, 4000, 5000, 6000].map((v) => {
        const a = (-120 + (v / max) * 240) * (Math.PI / 180);
        return (
          <line
            key={v}
            x1={cx + (r - 5) * Math.sin(a)}
            y1={cy - (r - 5) * Math.cos(a)}
            x2={cx + r * Math.sin(a)}
            y2={cy - r * Math.cos(a)}
            stroke={MUTED}
          />
        );
      })}
      <line
        x1={cx}
        y1={cy}
        x2={cx + (r - 7) * Math.sin(ang)}
        y2={cy - (r - 7) * Math.cos(ang)}
        stroke={HOT}
        strokeWidth={2.5}
        strokeLinecap="round"
      />
      <circle cx={cx} cy={cy} r={3} fill={INK} />
      <line x1={cx + r} y1={cy} x2={X0 - 4} y2={cy} stroke={INK} strokeWidth={4} />
      <HaloText
        x={cx}
        y={cy + r + 14}
        textAnchor="middle"
        fontSize={11}
        fontWeight={700}
        fill={INK}
      >
        {hPa(P)}
      </HaloText>
    </g>
  );
}

function SyringeFigure({
  s,
  mode,
  adv,
  inverse,
  clock,
}: {
  s: GasState;
  mode: Mode;
  adv: boolean;
  inverse: boolean;
  clock: number;
}) {
  const xR = X0 + s.V * PX_PER_ML;
  const xMax = X0 + 72 * PX_PER_ML;
  const speed = 46 * Math.sqrt(s.T / (THETA0 + T0));
  // graphique
  const L = 54;
  const Rr = W - 16;
  const T = 156;
  const B = 290;
  let xs: [number, number];
  let ys: [number, number];
  let fx: (st: GasState) => number;
  let fy: (st: GasState) => number;
  let xLabel: string;
  let yLabel: string;
  let law: GasState[];
  let through0 = false;
  if (!adv || mode === 'isotherm') {
    law = Array.from({ length: 101 }, (_, k) =>
      gasState('isotherm', V_RANGE[0] + (k / 100) * (V_RANGE[1] - V_RANGE[0]), 0),
    );
    if (inverse) {
      xs = [0, 1 / V_RANGE[0]];
      fx = (st) => 1 / st.V;
      xLabel = '1/V (mL⁻¹)';
      through0 = true;
    } else {
      xs = [0, V_RANGE[1]];
      fx = (st) => st.V;
      xLabel = 'V (mL)';
    }
    ys = [0, gasState('isotherm', V_RANGE[0], 0).P / 100];
    fy = (st) => st.P / 100;
    yLabel = 'P (hPa)';
  } else {
    law = Array.from({ length: 101 }, (_, k) =>
      gasState(mode, 0, THETA_RANGE[0] + (k / 100) * (THETA_RANGE[1] - THETA_RANGE[0])),
    );
    xs = [0, 450];
    fx = (st) => st.T;
    xLabel = 'T (K)';
    through0 = true;
    if (mode === 'isochor') {
      ys = [0, gasState('isochor', 0, 177).P / 100];
      fy = (st) => st.P / 100;
      yLabel = 'P (hPa)';
    } else {
      ys = [0, gasState('isobar', 0, 177).V];
      fy = (st) => st.V;
      yLabel = 'V (mL)';
    }
  }
  const X = (v: number) => L + ((v - xs[0]) / (xs[1] - xs[0])) * (Rr - L);
  const Y = (v: number) => B - ((v - ys[0]) / (ys[1] - ys[0])) * (B - T);
  const first = law[0]!;
  return (
    <svg
      viewBox={`0 0 ${W} 318`}
      className={`${FIGURE_BG_CLASS} w-full rounded-2xl`}
      role="img"
      aria-label={`Seringue : ${fr(s.V, 0)} mL d’air à ${hPa(s.P)} et ${celsius(s.T)}`}
      data-testid="gs-figure"
    >
      <Manometer P={s.P} />
      {/* corps de la seringue, graduations */}
      <rect
        x={X0}
        y={CY0}
        width={xMax - X0}
        height={CY1 - CY0}
        fill="none"
        stroke={INK}
        strokeWidth={1.5}
      />
      <rect x={X0} y={CY0} width={xR - X0} height={CY1 - CY0} fill={ACCENT} opacity={0.08} />
      {[10, 20, 30, 40, 50, 60, 70].map((v) => (
        <g key={v}>
          <line
            x1={X0 + v * PX_PER_ML}
            y1={CY0}
            x2={X0 + v * PX_PER_ML}
            y2={CY0 + 6}
            stroke={MUTED}
          />
          <text x={X0 + v * PX_PER_ML} y={CY0 - 4} textAnchor="middle" fontSize={9} fill={MUTED}>
            {v}
          </text>
        </g>
      ))}
      <text x={xMax} y={CY0 - 14} textAnchor="end" fontSize={9} fill={MUTED}>
        mL
      </text>
      {DOTS.map((d, i) => (
        <circle
          key={i}
          cx={bounce(X0 + 5 + d.x * (xR - X0 - 10) + speed * Math.cos(d.a) * clock, X0 + 4, xR - 4)}
          cy={bounce(
            CY0 + 5 + d.y * (CY1 - CY0 - 10) + speed * Math.sin(d.a) * clock,
            CY0 + 4,
            CY1 - 4,
          )}
          r={3.2}
          fill={ACCENT}
          data-testid="gs-dot"
        />
      ))}
      {/* piston */}
      <rect
        x={xR}
        y={CY0 + 1}
        width={7}
        height={CY1 - CY0 - 2}
        fill="var(--color-ink-400)"
        stroke={INK}
      />
      <rect
        x={xR + 7}
        y={(CY0 + CY1) / 2 - 3}
        width={W - 6 - xR - 7}
        height={6}
        fill="var(--color-ink-300)"
        stroke={INK}
      />
      <HaloText
        x={(X0 + xR) / 2}
        y={CY1 + 16}
        textAnchor="middle"
        fontSize={11}
        fontWeight={700}
        fill={INK}
      >
        {`${fr(s.V, 0)} mL`}
      </HaloText>
      {adv && (
        <HaloText x={xMax} y={CY1 + 16} textAnchor="end" fontSize={11} fill={HOT} fontWeight={600}>
          {`${celsius(s.T)} · ${kelvin(s.T)}`}
        </HaloText>
      )}
      {/* graphique */}
      <line x1={L} y1={B} x2={Rr} y2={B} stroke="var(--color-ink-300)" />
      <line x1={L} y1={T} x2={L} y2={B} stroke="var(--color-ink-300)" />
      <text x={L + 4} y={T - 6} fontSize={10} fontWeight={700} fill={INK}>
        {yLabel}
      </text>
      <text x={Rr} y={B + 22} textAnchor="end" fontSize={10} fill={MUTED}>
        {xLabel}
      </text>
      <text x={L - 4} y={T + 4} textAnchor="end" fontSize={9.5} fill={MUTED}>
        {fr(ys[1], 0)}
      </text>
      <text x={L - 4} y={B} textAnchor="end" fontSize={9.5} fill={MUTED}>
        0
      </text>
      <text
        x={adv && mode !== 'isotherm' ? L - 4 : L}
        y={B + 12}
        textAnchor={adv && mode !== 'isotherm' ? 'start' : 'middle'}
        fontSize={9.5}
        fill={MUTED}
      >
        {adv && mode !== 'isotherm' ? '0 K (−273,15 °C)' : '0'}
      </text>
      <text x={Rr} y={B + 12} textAnchor="end" fontSize={9.5} fill={MUTED}>
        {inverse && (!adv || mode === 'isotherm') ? '0,1' : fr(xs[1], 0)}
      </text>
      {through0 && (
        <line
          x1={X(0)}
          y1={Y(0)}
          x2={X(fx(first))}
          y2={Y(fy(first))}
          stroke={ACCENT}
          strokeWidth={1.5}
          strokeDasharray="4 4"
        />
      )}
      <polyline
        points={law.map((st) => `${X(fx(st))},${Y(fy(st))}`).join(' ')}
        fill="none"
        stroke={ACCENT}
        strokeWidth={2.2}
      />
      <circle cx={X(fx(s))} cy={Y(fy(s))} r={5.5} fill={HOT} stroke="white" strokeWidth={1.5} />
    </svg>
  );
}

function SyringeView({ rank, still }: { rank: number | null; still: boolean }) {
  const adv = idealGas(rank);
  const [mode, setMode] = useState<Mode>('isotherm');
  const [V, setV] = useState(V0_ML);
  const [theta, setTheta] = useState(THETA0);
  const [inverse, setInverse] = useState(false);
  const m = adv ? mode : 'isotherm';
  const s = gasState(m, V, theta);
  const clock = useClock(still);
  const rows: Array<[string, string]> = [
    ['Pression P', adv ? `${hPa(s.P)} = ${bar(s.P)}` : hPa(s.P)],
    ['Volume V', `${fr(s.V, 1)} mL`],
  ];
  if (!adv) {
    rows.push(['Température', `${THETA0} °C (constante)`]);
    rows.push(['P × V', `${fr((s.P / 100) * s.V, 0)} hPa·mL`]);
  } else {
    rows.push(['Température T', `${kelvin(s.T)} (${celsius(s.T)})`]);
    rows.push(['Quantité de matière n', `${fr(N_AIR * 1000, 2)} × 10⁻³ mol`]);
    if (m === 'isotherm') rows.push(['P × V', `${fr(s.P * s.V * 1e-6, 2)} J`]);
    if (m === 'isochor') rows.push(['P ÷ T', `${fr(s.P / s.T, 1)} Pa/K`]);
    if (m === 'isobar') rows.push(['V ÷ T', `${fr(s.V / s.T, 3)} mL/K`]);
    rows.push(['Volume molaire V_m = V ÷ n', `${fr(molarVolume(s), 1)} L/mol`]);
    rows.push(['Masse volumique de l’air', `${fr(airDensity(s), 2)} g/L`]);
  }
  return (
    <div className="space-y-4">
      {adv && (
        <Segmented
          ariaLabel="Transformation"
          value={mode}
          options={MODES.map((x) => ({ value: x.id, label: x.label }))}
          onChange={(v) => {
            setMode(v);
            setV(V0_ML);
            setTheta(THETA0);
          }}
        />
      )}
      <SyringeFigure s={s} mode={m} adv={adv} inverse={inverse} clock={clock} />
      {m === 'isotherm' ? (
        <ParamSlider
          name="Volume (piston)"
          ariaLabel="Volume"
          value={V}
          min={V_RANGE[0]}
          max={V_RANGE[1]}
          step={1}
          onChange={setV}
          format={(v) => frenchSpaces(`${fr(v, 0)} mL`)}
        />
      ) : (
        <ParamSlider
          name="Température θ"
          ariaLabel="Température"
          value={theta}
          min={THETA_RANGE[0]}
          max={THETA_RANGE[1]}
          step={5}
          onChange={setTheta}
          format={(v) => frenchSpaces(`${fr(v, 0)} °C`)}
          color={HOT}
        />
      )}
      {m === 'isotherm' && (
        <ToggleChip on={inverse} onClick={() => setInverse(!inverse)}>
          Tracer P en fonction de 1/V
        </ToggleChip>
      )}
      <Readout rows={rows} />
      <Lines lines={syringeLines(m, s, rank)} />
    </div>
  );
}

/* ---------- profondeur ---------- */

const D_TOP = 34;
const PX_PER_M = 6.3;
const MAX_DEPTH = 40;

function DepthFigure({ rho, h, equalized }: { rho: number; h: number; equalized: boolean }) {
  const yOf = (d: number) => D_TOP + d * PX_PER_M;
  const P = depthPressure(rho, h);
  const F = equalized ? 0 : rho * G * h * EARDRUM;
  const y = yOf(h);
  const x = 176;
  return (
    <svg
      viewBox={`0 0 ${W} ${yOf(MAX_DEPTH) + 14}`}
      className={`${FIGURE_BG_CLASS} w-full rounded-2xl`}
      role="img"
      aria-label={`Plongeur à ${h} m : pression ${bar(P)}`}
      data-testid="gs-depth"
    >
      <rect
        x={52}
        y={D_TOP}
        width={W - 60}
        height={yOf(MAX_DEPTH) - D_TOP}
        fill="#3b82f6"
        opacity={0.16}
      />
      <line x1={52} y1={D_TOP} x2={W - 8} y2={D_TOP} stroke="#3b82f6" strokeWidth={2} />
      {[0, 10, 20, 30, 40].map((d) => (
        <g key={d}>
          <line x1={46} y1={yOf(d)} x2={56} y2={yOf(d)} stroke={INK} />
          <text x={42} y={yOf(d) + 3.5} textAnchor="end" fontSize={10} fill={INK}>
            {`${d} m`}
          </text>
          <text
            x={W - 12}
            y={yOf(d) + (d === 0 ? -5 : 3.5)}
            textAnchor="end"
            fontSize={9.5}
            fill={MUTED}
          >
            {bar(depthPressure(rho, d))}
          </text>
        </g>
      ))}
      <line x1={70} y1={D_TOP} x2={70} y2={y} stroke={HOT} strokeWidth={2} strokeDasharray="4 3" />
      <HaloText x={74} y={(D_TOP + y) / 2 + 4} fontSize={10} fill={HOT} fontWeight={600}>
        {h > 3 ? `h = ${h} m` : ''}
      </HaloText>
      {/* plongeur */}
      <g data-testid="gs-diver">
        <ellipse cx={x + 22} cy={y + 2} rx={22} ry={7} fill="var(--color-ink-700)" />
        <path d={`M ${x + 42} ${y} l 16 -6 l 0 14 Z`} fill="var(--color-warning-500)" />
        <rect x={x + 6} y={y - 12} width={22} height={7} rx={3} fill="var(--color-ink-400)" />
        <circle cx={x - 6} cy={y} r={9} fill="#fcd9b6" stroke={INK} />
        <rect x={x - 13} y={y - 5} width={10} height={6} rx={2} fill="#93c5fd" stroke={INK} />
      </g>
      {F > 0.05 && (
        <g data-testid="gs-ear-force">
          <line
            x1={x - 6}
            y1={y - 26 - Math.min(30, 2 * F)}
            x2={x - 6}
            y2={y - 11}
            stroke={HOT}
            strokeWidth={2.5}
          />
          <path d={`M ${x - 11} ${y - 16} L ${x - 1} ${y - 16} L ${x - 6} ${y - 9} Z`} fill={HOT} />
          <HaloText x={x + 2} y={y - 22} fontSize={10} fontWeight={700} fill={HOT}>
            {`F = ${fr(F, 1)} N`}
          </HaloText>
        </g>
      )}
      <HaloText x={x - 20} y={y + 26} fontSize={11} fontWeight={700} fill={INK}>
        {`P = ${bar(P)}`}
      </HaloText>
    </svg>
  );
}

function DepthView() {
  const [liq, setLiq] = useState(LIQUIDS[0]!.id);
  const [h, setH] = useState(10);
  const [equalized, setEqualized] = useState(false);
  const rho = LIQUIDS.find((l) => l.id === liq)!.rho;
  const dP = rho * G * h;
  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Liquide"
        value={liq}
        options={LIQUIDS.map((l) => ({ value: l.id, label: l.label }))}
        onChange={setLiq}
      />
      <DepthFigure rho={rho} h={h} equalized={equalized} />
      <ParamSlider
        name="Profondeur h"
        ariaLabel="Profondeur"
        value={h}
        min={0}
        max={MAX_DEPTH}
        step={1}
        onChange={setH}
        format={(v) => frenchSpaces(`${fr(v, 0)} m`)}
      />
      <ToggleChip on={equalized} onClick={() => setEqualized(!equalized)}>
        Oreille équilibrée
      </ToggleChip>
      <Readout
        rows={[
          ['Pression P', `${hPa(P_ATM + dP)} = ${bar(P_ATM + dP)}`],
          ['ΔP = ρ × g × h', `${fr(dP, 0)} Pa`],
          [
            'Force sur le tympan (0,5 cm²)',
            equalized ? '0 N (équilibrée)' : `${fr(dP * EARDRUM, 1)} N`,
          ],
        ]}
      />
      <Lines lines={depthLines(rho, h, equalized)} />
    </div>
  );
}

function Explore({ rank, still }: { rank: number | null; still: boolean }) {
  const parts = partsFor(rank);
  const [part, setPart] = useState<Part>(parts[0]!.id);
  return (
    <div className="space-y-4">
      {parts.length > 1 && (
        <Segmented
          ariaLabel="Partie"
          value={part}
          options={parts.map((p) => ({ value: p.id, label: p.label }))}
          onChange={setPart}
        />
      )}
      {part === 'syringe' && <SyringeView rank={rank} still={still} />}
      {part === 'depth' && <DepthView />}
    </div>
  );
}

export function GasTool({
  level = null,
  seed,
}: {
  /** niveau : 1re (Mariotte, profondeur) ou Tle (gaz parfait) */
  level?: string | null;
  /** graine du défi (tests) ; elle fige aussi l'agitation des particules */
  seed?: number;
}) {
  const rank = schoolLevelRank(level);
  const [mode, setMode] = useState<'explore' | 'quiz'>('explore');
  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Partie de l’outil"
        value={mode}
        options={[
          { value: 'explore', label: 'Explorer' },
          { value: 'quiz', label: 'Défi' },
        ]}
        onChange={setMode}
      />
      <div hidden={mode !== 'explore'}>
        <Explore rank={rank} still={seed !== undefined || mode !== 'explore'} />
      </div>
      <div hidden={mode !== 'quiz'}>
        <McqChallenge kinds={quizKinds(rank)} make={makeQuiz} seed={seed} testId="gs" rich />
      </div>
    </div>
  );
}
